import {
  BADGE_LAYOUT_BASE,
  type BadgeSize,
  type BadgeTone,
  badgeSizeClass,
  badgeToneColorClass,
} from "components/ui/StatusBadge/StatusBadge";
import { cn } from "lib/utils";
import type { ComponentType, HTMLAttributes } from "react";

/**
 * Badge の color プロパティ → StatusBadge の tone へのマッピング。
 * 旧 DaisyUI ベースの API を維持しつつ、内部的には自前のソフトパステル
 * トーン体系を使うため、画面全体で見た目が統一される。
 */
const COLOR_TO_TONE: Record<NonNullable<BadgeProps["color"]>, BadgeTone> = {
  default: "soft-neutral",
  primary: "tag-neutral",
  secondary: "tag-neutral",
  accent: "tag-neutral",
  ghost: "soft-neutral",
  info: "soft-info",
  success: "soft-success",
  warning: "soft-warning",
  error: "soft-error",
};

export type BadgeColor =
  | "default"
  | "primary"
  | "secondary"
  | "accent"
  | "ghost"
  | "info"
  | "success"
  | "warning"
  | "error";

/** lucide-react を含む汎用アイコン型 (size を受け取る) */
type IconComponent = ComponentType<{
  size?: number | string;
  "aria-hidden"?: boolean | "true" | "false";
}>;

/** size に応じたアイコンの px サイズ */
const ICON_PX_BY_SIZE: Record<BadgeSize, number> = {
  xs: 10,
  sm: 12,
  md: 14,
  lg: 16,
};

export type BadgeProps = {
  color?: BadgeColor;
  size?: BadgeSize;
  isOutline?: boolean;
  /**
   * BadgeTone 直接指定. 指定された場合 color を無視する.
   * stage-1〜final / critical-* など、 color マッピングに無い色を使うときの escape hatch.
   */
  tone?: BadgeTone;
  /** 任意のアイコン (lucide-react 等). children の前に並ぶ. */
  icon?: IconComponent;
} & Omit<HTMLAttributes<HTMLSpanElement>, "className">;

/**
 * 共通 Badge リーフ。後方互換のために color/size/isOutline を露出するが、
 * 内部的には自前ソフトパステル tone (StatusBadge) を使うので、
 * 画面横断で badge の見た目が統一される。
 *
 * size:
 *   - xs: 最小. 一覧 1 行内のステータス / parent タグ用
 *   - sm: 既定. 詳細パネル等
 *   - md / lg: 強調表示用
 *
 * @param {BadgeProps} props Badge の props
 * @return {JSX.Element} Badge 要素
 */
export const Badge = ({
  color = "default",
  size = "sm",
  tone: toneOverride,
  icon: Icon,
  children,
  ...rest
}: BadgeProps): JSX.Element => {
  const tone = toneOverride ?? COLOR_TO_TONE[color];
  return (
    <span
      className={cn(
        BADGE_LAYOUT_BASE,
        badgeSizeClass[size],
        badgeToneColorClass[tone],
      )}
      {...rest}
    >
      {Icon && <Icon size={ICON_PX_BY_SIZE[size]} aria-hidden />}
      {children}
    </span>
  );
};
